// Launcher side of the postMessage protocol. Sends Launcher→Panel envelopes
// to the iframe and listens for Panel→Launcher messages.

import {
  isPanelToLauncher,
  makeEnvelope,
  type InitPayload,
  type LauncherToPanel,
  type OpenPayload,
  type PanelToLauncher,
  type SiteContext,
  type ThemeName,
  type UserHint,
} from '@ba/protocol';

export interface PanelBridgeOptions {
  iframe: HTMLIFrameElement;
  /** Defaults to '*' — we accept any panel origin since the launcher controls the URL. */
  targetOrigin?: string;
}

export type PanelEventHandler = (event: PanelToLauncher) => void;

export function createPanelBridge(opts: PanelBridgeOptions): {
  send: (env: LauncherToPanel) => void;
  sendInit: (payload: InitPayload) => void;
  sendOpen: (payload: OpenPayload) => void;
  sendClose: () => void;
  sendUpdateUser: (user: UserHint) => void;
  sendUpdateContext: (siteContext: SiteContext) => void;
  sendUpdateTheme: (theme: ThemeName) => void;
  onPanelMessage: (handler: PanelEventHandler) => () => void;
} {
  const targetOrigin = opts.targetOrigin ?? '*';

  function postEnv(env: LauncherToPanel): void {
    const win = opts.iframe.contentWindow;
    if (win === null) return;
    win.postMessage(env, targetOrigin);
  }

  return {
    send(env) {
      postEnv(env);
    },
    sendInit(payload) {
      postEnv(makeEnvelope('init', payload));
    },
    sendOpen(payload) {
      postEnv(makeEnvelope('open', payload));
    },
    sendClose() {
      postEnv(makeEnvelope('close', {}));
    },
    sendUpdateUser(user) {
      postEnv(makeEnvelope('update-user', { user }));
    },
    sendUpdateContext(siteContext) {
      postEnv(makeEnvelope('update-context', { siteContext }));
    },
    sendUpdateTheme(theme) {
      postEnv(makeEnvelope('update-theme', { theme }));
    },
    onPanelMessage(handler) {
      const listener = (ev: MessageEvent): void => {
        if (ev.source !== opts.iframe.contentWindow) return;
        if (!isPanelToLauncher(ev.data)) return;
        handler(ev.data);
      };
      window.addEventListener('message', listener);
      return () => window.removeEventListener('message', listener);
    },
  };
}
